import React, { useId } from "react";

import {
  ButtonWithPopOut,
  constants,
  useButtonWithPopOut,
  useWindowSize,
} from "@newsela/angelou";
import {
  withTrackEvent,
  useTracker as useTrackerSnitch,
} from "@newsela/snitch";
import { forEach, isEmpty, map, toNumber } from "lodash-es";
import PropTypes from "prop-types";

import useUpdateEffect from "@/utils/useUpdateEffect";
import {
  labelCss,
  menuChoice,
  menuLabel,
  menuOptionsContainer,
  selectedBar,
  selectedMenuOption,
} from "./ButtonWithPopOutMenu.styles";

const ButtonWithPopOutWithTrackEvent = withTrackEvent(ButtonWithPopOut);

const propTypes = {
  buttonContents: PropTypes.node.isRequired,
  currentOptionValue: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
      languageAttribute: PropTypes.string,
      dataQaSelector: PropTypes.string,
    }),
  ).isRequired,
  onClickButton: PropTypes.func,
  onSelectedOption: PropTypes.func,
  ariaProps: PropTypes.shape({
    buttonAriaProps: PropTypes.object,
    menuOptionsProps: PropTypes.object,
  }),
  __cssFor: PropTypes.shape({
    root: PropTypes.object,
    Button: PropTypes.object,
    PopOut: PropTypes.object,
  }),
  /**
   * i dont think these are truly required...
   */
  objectType: PropTypes.string,
  actionPrefix: PropTypes.string,
  actionName: PropTypes.string,
  selectedLabelKey: PropTypes.string,
  streamProperties: PropTypes.object,

  /** Optional: Ref object to the button, used for focus management when the popout opens/closes.
   * If not provided, a ref will be created internally.
   */
  buttonRef: PropTypes.object,
  label: PropTypes.string,
  labelKey: PropTypes.string,
};

const ButtonWithPopOutMenu = (props) => {
  const {
    buttonContents,
    currentOptionValue,
    menuOptions,
    onClickButton,
    onSelectedOption,
    ariaProps,
    __cssFor,
    objectType,
    actionPrefix,
    actionName,
    selectedLabelKey = null,
    streamProperties,
    buttonRef: externalButtonRef,
    label,
    labelKey,
  } = props;
  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { sm },
    },
  } = constants;
  const isMobile = windowWidth <= sm;

  const internalButtonRef = React.useRef();
  const buttonRef = externalButtonRef ?? internalButtonRef;
  const controllingProps = useButtonWithPopOut({ buttonRef });

  const id = useId();

  // These utility selectors will be based on the unique ID prop
  const selectedMenuId = `${id}_selected`;
  const menuItemClassName = `${id}_class`;
  const { trackAction, getEventName } = useTrackerSnitch();

  const setFocusToElement = () => {
    const selectedMenuItem = document.getElementById(selectedMenuId);

    if (selectedMenuItem) {
      selectedMenuItem.focus({ preventScroll: true });
    } else {
      const firstMenuItem = document.getElementById(
        `${id}-listbox`,
      )?.firstElementChild;
      if (firstMenuItem) {
        firstMenuItem.focus();
      }
    }
  };

  // When popout becomes visible, focus the appropriate menu item
  useUpdateEffect(() => {
    if (controllingProps.isPopOutVisible) {
      setFocusToElement();
    } else {
      // Else, if the menu is closed, apply focus to the button.
      buttonRef?.current?.focus({ preventScroll: true });
    }
  }, [controllingProps.isPopOutVisible]);

  const handleKeyDownEventForButton = (event) => {
    const isArrowUpKey = event.key === "ArrowUp";
    const isArrowDownKey = event.key === "ArrowDown";

    if (isArrowUpKey || isArrowDownKey) {
      event.preventDefault();
      controllingProps.setIsPopOutVisible(true);
    }
  };

  const handleSelection = (menuOption) => {
    controllingProps.setIsPopOutVisible(false);
    if (onSelectedOption) {
      onSelectedOption(menuOption.value);
    }
  };

  const applyFocusToOption = (listItemIndexNumber) => {
    const optionsAsNodes = document.querySelectorAll('[role="option"]');

    if (isEmpty(optionsAsNodes)) {
      return;
    }

    forEach(optionsAsNodes, (optionNode) => {
      if (
        toNumber(optionNode.getAttribute("data-index-number")) ===
        listItemIndexNumber
      ) {
        optionNode.focus();
      }
    });
  };

  const handleKeyDownEventForMenuItem = (
    event,
    listItemIndexNumber,
    menuOption,
  ) => {
    const isArrowUpKey = event.key === "ArrowUp";
    const isArrowDownKey = event.key === "ArrowDown";
    const isEnterKey = event.key === "Enter";
    const isSpaceKey = event.key === " ";
    const isTabKey = event.key === "Tab";

    if (!isTabKey) {
      event.preventDefault();
    }

    if (isArrowUpKey) {
      // Apply focus to the previous menu option in the menu.
      applyFocusToOption(listItemIndexNumber - 1);
    } else if (isArrowDownKey) {
      applyFocusToOption(listItemIndexNumber + 1);
      // Apply focus to the next menu option in the menu.
    } else if (isEnterKey || isSpaceKey) {
      handleSelection(menuOption);
    } else if (isTabKey) {
      controllingProps.setIsPopOutVisible(false);
    }
  };

  const isOptionSelected = (menuOption, currentOptionValue) => {
    // The Read/Article page has yet to be ported over to Lyceum. But when it is,
    // the component that renders the article's lexile-level list will presumably
    // make use of this component (since the monolith-Article page makes use
    // of the monolith-equivalent of this component). The following code pertains
    // to the component that will compose this component to render the lexile-level
    // menu-options.
    if (
      JSON.stringify.currentOptionValue?.includes("MAX") &&
      menuOption?.label?.includes("MAX")
    ) {
      // We use the "includes" method to handle when the lexile-level option is
      // MAX and the article is also is available in more than one langauge,
      // i.e., "Spanish MAX"
      return true;
    }

    // Same here as above. This is code that pertains to the component that
    // composes this component to render the Read/Article page's lexile-level list.
    const menuOptionLexile = menuOption.lexile_level;
    if (menuOptionLexile) {
      return `${menuOptionLexile}L` === currentOptionValue;
    }

    return menuOption.value === currentOptionValue;
  };

  const popOutContents = (
    <ul
      css={menuOptionsContainer}
      tabIndex="-1"
      role="listbox"
      id={`${id}-listbox`}
      aria-labelledby={`${id}-label`}
      {...ariaProps?.menuOptionsProps}
    >
      {map(menuOptions, (menuOption, index) => {
        const optionLabel = menuOption.label;
        const selectedOption = isOptionSelected(menuOption, currentOptionValue);
        const dataQaSelector = menuOption.dataQaSelector || menuOption.label;
        const dataValue = menuOption.dataValue ? menuOption.dataValue : null;
        const selectedMenu =
          selectedOption === true ? selectedMenuOption : menuChoice;
        const menuOptionEventProperties = {
          actionName,
          objectType,
          event_name: getEventName(objectType, null, actionName),
          dimension_properties: {
            ...streamProperties,
            ...(selectedLabelKey && { [selectedLabelKey]: optionLabel }),
          },
        };
        return (
          <li
            css={selectedMenu}
            className={`${menuItemClassName}`}
            onClick={() => {
              handleSelection(menuOption);
              trackAction(menuOptionEventProperties);
            }}
            role="option"
            key={`${optionLabel}${index}`}
            aria-selected={selectedOption}
            data-index-number={index}
            id={selectedOption ? selectedMenuId : optionLabel}
            tabIndex={-1}
            onKeyDown={(event) =>
              handleKeyDownEventForMenuItem(event, index, menuOption)
            }
            value={dataValue}
          >
            {selectedOption && <div css={selectedBar} />}
            <span
              data-qa-selector={dataQaSelector}
              css={menuLabel}
              lang={menuOption.languageAttribute}
            >
              {optionLabel}
            </span>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div>
      {label && (
        <div id={`${id}-label`} css={labelCss}>
          {label}
        </div>
      )}
      <ButtonWithPopOutWithTrackEvent
        buttonContents={buttonContents}
        popOutContents={popOutContents}
        onKeyDown={(event) => handleKeyDownEventForButton(event)}
        id={id}
        ref={buttonRef}
        {...controllingProps}
        onClickButton={(e) =>
          onClickButton
            ? onClickButton(e) && controllingProps.onClickButton(e)
            : controllingProps.onClickButton(e)
        }
        ariaProps={{
          "aria-haspopup": "listbox",
          "aria-controls": `${id}-listbox`,
          ...ariaProps?.buttonAriaProps,
        }}
        __cssFor={__cssFor}
        horizontalPopOutPosition={isMobile ? "RIGHT" : "LEFT"}
        actionName={actionName}
        actionPrefix={actionPrefix}
        objectType={objectType}
        streamProperties={streamProperties}
        dataProps={{
          "data-qa-selector": `${labelKey ?? label}-buttonWithPopoutMenu`,
        }}
      />
    </div>
  );
};

ButtonWithPopOutMenu.propTypes = propTypes;
ButtonWithPopOutMenu.displayName = "ButtonWithPopOutMenu";

export default ButtonWithPopOutMenu;
